import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileUp, Loader2, KeyRound, Trash2, ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmButton } from "@/components/ConfirmButton";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PayslipArchive } from "@/components/batch/PayslipArchive";
import { EmployeeSheet } from "@/components/EmployeeSheet";

const Dropzone = ({ file, setFile }) => {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };
  return (
    <div
      data-testid="pdf-upload-dropzone"
      onClick={() => ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`ledger-grid flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-14 text-center transition-colors duration-150 ${
        over ? "border-[#5e9d6b] bg-[#eef6ef]" : "border-slate-300 bg-white hover:border-slate-400"
      }`}
    >
      <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" data-testid="pdf-file-input"
        onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} />
      <div className="grid h-12 w-12 place-items-center rounded-full bg-[#3d6e49] text-white"><FileUp className="h-5 w-5" /></div>
      {file ? (
        <p className="font-medium text-slate-900" data-testid="selected-file-name">{file.name}</p>
      ) : (
        <>
          <p className="font-medium text-slate-900">Trascina qui il PDF dei cedolini</p>
          <p className="text-sm text-slate-500">oppure clicca per sceglierlo · max 50 MB</p>
        </>
      )}
    </div>
  );
};

const BatchList = ({ batches, onDelete }) => (
  <div className="panel overflow-hidden" data-testid="batch-list">
    <div className="border-b border-slate-100 px-6 py-4"><p className="eyebrow">Caricamenti precedenti</p></div>
    {batches.length === 0 ? (
      <p className="px-6 py-6 text-sm text-slate-500">Nessun caricamento.</p>
    ) : (
      <ul className="divide-y divide-slate-100">
        {batches.map((b) => (
          <li key={b.id} className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`batch-row-${b.id}`}>
            <div className="min-w-0">
              <p className="font-medium text-slate-900">{b.period || "Periodo non rilevato"}</p>
              <p className="truncate text-xs text-slate-500">{b.filename} · {formatDate(b.created_at)} · {b.summary.total} buste · {b.summary.sent} inviate</p>
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="outline" data-testid={`open-batch-${b.id}`}>
                <Link to={`/cedolini/${b.id}`}>Apri <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
              </Button>
              <ConfirmButton
                testId={`delete-batch-${b.id}`}
                title="Eliminare il caricamento?"
                description="Verranno eliminati i PDF divisi. Lo storico degli invii resta disponibile."
                onConfirm={() => onDelete(b.id)}
              >
                <Trash2 className="h-4 w-4" />
              </ConfirmButton>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function Upload() {
  const [openEmp, setOpenEmp] = useState(null);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [hasSaved, setHasSaved] = useState(false);
  const [autoSend, setAutoSend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);

  const load = () => api.get("/batches").then((r) => setBatches(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => { setHasSaved(r.data.has_pdf_password); setAutoSend(r.data.auto_send); }).catch(() => {});
  }, []);

  const process = async () => {
    if (!file) return toast.error("Seleziona prima un file PDF");
    const form = new FormData();
    form.append("file", file);
    form.append("password", password);
    setLoading(true);
    try {
      const { data } = await api.post("/batches", form);
      const auto = data.auto_send;
      if (auto?.attempted) {
        const msg = `Invio automatico: ${auto.sent} inviate${auto.failed ? `, ${auto.failed} fallite` : ""}`;
        auto.failed ? toast.warning(msg) : toast.success(msg);
      } else if (autoSend && auto?.reason === "abbinamenti_incompleti") {
        toast.warning(`${data.summary.total} buste trovate: ${data.summary.issues} senza abbinamento, invio automatico sospeso`);
      } else if (autoSend && auto?.reason === "smtp_non_configurato") {
        toast.warning("Invio automatico non eseguito: configura prima il server SMTP");
      } else {
        toast.success(`PDF elaborato: ${data.summary.total} buste paga trovate`);
      }
      navigate(`/cedolini/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/batches/${id}`);
      toast.success("Caricamento eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <PageHeader eyebrow="Cedolini" title="Carica il PDF del mese"
        description="Il file viene sbloccato con la password, diviso per dipendente e abbinato all'anagrafica tramite codice fiscale o nome. L'invio richiede sempre la conferma dell'amministratore."
        actions={<Button asChild variant="outline" data-testid="history-link-button"><Link to="/cedolini/storico">Storico invii</Link></Button>} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-8 animate-rise delay-1">
          <Dropzone file={file} setFile={setFile} />
        </div>
        <div className="panel space-y-5 p-6 lg:col-span-4 animate-rise delay-2">
          <div className="space-y-2">
            <Label htmlFor="pdf-pw" className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Password del PDF</Label>
            <Input id="pdf-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={hasSaved ? "Usa la password salvata" : "Inserisci la password"} data-testid="pdf-password-input" />
            <p className="text-xs text-slate-500">
              {hasSaved ? "Lascia vuoto per usare quella salvata nelle Impostazioni." : "Verrà salvata per i prossimi caricamenti."}
            </p>
          </div>
          <Button onClick={process} disabled={loading || !file} className="w-full active:scale-[0.98]" data-testid="process-pdf-button">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}
            {loading ? "Elaborazione in corso…" : "Elabora PDF"}
          </Button>
          {autoSend ? (
            <p className="flex items-start gap-2 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="auto-send-notice">
              <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Invio automatico attivo: se tutte le buste risultano abbinate, le email partono subito.
            </p>
          ) : (
            <p className="text-xs text-slate-500">Prima dell'invio potrai controllare l'elenco e correggere gli abbinamenti.</p>
          )}
        </div>
      </div>
      <BatchList batches={batches} onDelete={remove} />
      <PayslipArchive onOpenEmployee={setOpenEmp} />
      <EmployeeSheet employeeId={openEmp} onClose={() => setOpenEmp(null)} />
    </>
  );
}
